import { GlobalSettingsConfig, LANGUAGES } from '@rc-d2c/context-manager';
import { logAndNotifyError } from '@rc-d2c/context-manager/src/utils/errorHandler';
import React, { PropsWithChildren, useEffect } from 'react';
import { I18nextProvider } from 'react-i18next';
import { useStore } from 'zustand';

import { getTranslationsService } from './getTranslationsService';
import { i18nInstance } from './index';
import { COUNTRY_FALLBACK_LOCALES, DEFAULT_LOCALE, SUPPORTED_LOCALES } from './locales.constants';

const countryHasFallback = (item?: string): item is keyof typeof COUNTRY_FALLBACK_LOCALES =>
  COUNTRY_FALLBACK_LOCALES[item as keyof typeof COUNTRY_FALLBACK_LOCALES] !== undefined;

const I18nProvider: React.ElementType = ({ children }: PropsWithChildren) => {
  const language = useStore(GlobalSettingsConfig.GlobalSettingsStore, (state) => state.language);

  useEffect(() => {
    getTranslationFiles(language);
  }, [language]);

  const getTranslationFiles = async (lang: string) => {
    const shortLang = lang.split('-')[0];

    let langForTranslations = '';
    let langForServer: string = '';
    if (SUPPORTED_LOCALES.includes(lang as LANGUAGES)) {
      langForTranslations = lang;
      langForServer = lang;
    } else if (countryHasFallback(shortLang)) {
      langForTranslations = shortLang;
      langForServer = COUNTRY_FALLBACK_LOCALES[shortLang];
    } else {
      langForTranslations = DEFAULT_LOCALE;
      langForServer = DEFAULT_LOCALE;
    }

    try {
      const translations = await getTranslationsService(langForServer);
      i18nInstance.addResourceBundle(langForTranslations, 'translation', translations);
      i18nInstance.changeLanguage(langForTranslations);
    } catch (error) {
      logAndNotifyError('I18nProvider', error, false);
    }
  };

  return <I18nextProvider i18n={i18nInstance}>{children}</I18nextProvider>;
};

export default I18nProvider;
